import styles from './styles.module.scss'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'

import { showBanner as showBannerAction } from '@store/redux/actions/otherActions'
import Notification, { NotificationProps } from '@components/ui/Notification'
import ButtonMinimal from '@components/ui/Button/ButtonMinimal'
import { useCart } from '@components/cart/hooks'

export const NOTIFICATION_BANNERS = {
  'Discount Code - SORTED':
    'Your 10% discount will be applied at the checkout.',
  'Discount Code - OTHER': 'Your discount will be applied at the checkout.',
  'OOSModal - success':
    'Thank you! We will be in touch when this product becomes available.',
  'Feedback Form - success': 'Thank you for your feedback',
  'Feedback Form - failed': 'We think you might have missed a question',
  'Unsubscribe From Catalog Form - success':
    'Thank you. You will stop receiving our catalogue shortly.',
  'Unsubscribe From Catalog Form - failed':
    'We think you might have missed a question',
  'Add to basket - failed':
    "There was a problem adding this product to basket. Please try again, if that doesn't work please contact Customer Service.",
  'Add to basket - variant problem':
    'Sorry, this product is now out of stock. Please try another size or submit a Back in Stock notification request.',
  'Add to basket - Out of stock':
    'Sorry, this colour is now out of stock. Please try another colour or submit a Back in Stock notification request.'
} as const

export type NotificationKey = keyof typeof NOTIFICATION_BANNERS
export type NotificationType = NotificationKey | undefined

export type PushNotificationBannerProps = {
  isVisible: boolean
  type: NotificationType | string
  theme: NotificationProps['type']
}

export function InvalidCheckoutNotification() {
  const { navigateToCheckout } = useCart()

  return (
    <p>
      <strong>We need a quick fix to your checkout details:</strong> Some of
      your checkout details are invalid. Please{' '}
      <ButtonMinimal
        underlined
        variant="white"
        onClick={e =>
          navigateToCheckout({
            event: e,
            triggerButtonReady: async () => true,
            triggerButtonSuccess: async () => true,
            triggerButtonError: async () => true
          })
        }
      >
        update your information
      </ButtonMinimal>{' '}
      and try again.
    </p>
  )
}

const BANNER_RENDERERS: Partial<Record<string, () => React.ReactNode>> = {
  'Fix Checkout Details': () => <InvalidCheckoutNotification />
}

export default function PushNotificationBanner({
  isVisible,
  type,
  theme
}: PushNotificationBannerProps) {
  const dispatch = useDispatch()

  const content = type
    ? BANNER_RENDERERS[type]?.() ??
      NOTIFICATION_BANNERS[type as keyof typeof NOTIFICATION_BANNERS]
    : null

  const notificationBannerStyles = classNames(
    styles.notificationBannerWrapper,
    {
      [styles.showNotificationBanner]: isVisible
    }
  )

  const isDismissable = !!type && theme !== 'success'

  const closeBanner = () =>
    dispatch(
      showBannerAction({
        isVisible: false,
        type,
        theme: 'default'
      })
    )

  return content ? (
    <div className={notificationBannerStyles}>
      <Notification
        classes={`${styles.notificationHeader} ${
          isDismissable ? styles.dismissable : ''
        }`}
        type={theme}
        theme="dark"
        variant="alert"
        content={content}
        dismissible={isDismissable}
        onDismiss={closeBanner}
      />
    </div>
  ) : null
}
